import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  assignBillingSubscription,
  excludePlatformDominio,
  getBillingPlanCatalog,
  getPlatformDominioAdminDetail,
  getPlatformDominioOfferAnalytics,
  getPlatformDominioUsageSummary,
  getPlatformDominioWorkspaceSegment,
  listPlatformBillingAudit,
  listPlatformDominioInvoices,
  setPlatformDominioWorkspaceSegment,
} from "../../../api/client";
import { ALL_WORKSPACE_SEGMENTS, WorkspaceSegments } from "../../../constants/workspaceSegments";

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("pt-BR");
}

function centsToBrl(c) {
  const n = Number(c || 0) / 100;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Mesma convenção da API: subdomínio único + .xpro.me → nome do schema no tenant. */
function inferSchemaFromHost(dominioRaw) {
  const dom = String(dominioRaw || "").trim().toLowerCase();
  if (!dom) return "";
  const host = dom.replace(/^https?:\/\//i, "").split("/")[0].split(":")[0];
  if (!host || !host.endsWith(".xpro.me")) return "";
  const schema = host.slice(0, -".xpro.me".length);
  if (!schema || schema.includes(".")) return "";
  return schema;
}

function subscriptionStatusChipColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "trialing") return "info";
  if (s === "past_due") return "warning";
  if (s === "suspended" || s === "cancelled") return "error";
  return "default";
}

function OverviewField({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25, letterSpacing: 0.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {children}
      </Typography>
    </Box>
  );
}

const WorkspaceDetailPage = () => {
  const { dominioId: dominioIdParam } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("app");
  const dominioId = String(dominioIdParam || "").trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [detail, setDetail] = useState(null);
  const [innerTab, setInnerTab] = useState(0);

  const [usageData, setUsageData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [offerAnalytics, setOfferAnalytics] = useState([]);
  const [audit, setAudit] = useState([]);

  const [segmentValue, setSegmentValue] = useState(WorkspaceSegments.Generic);
  const [segmentSaving, setSegmentSaving] = useState(false);

  const [planCatalog, setPlanCatalog] = useState([]);
  const [assignForm, setAssignForm] = useState({
    planSku: "",
    version: 1,
    billingMode: "postpaid",
    enforceLimits: false,
    status: "active",
  });
  const [assigning, setAssigning] = useState(false);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excluding, setExcluding] = useState(false);

  const billingModeLabel = useCallback(
    (mode) =>
      t(`billingPage.billingMode.${String(mode || "").toLowerCase()}`, {
        defaultValue: String(mode || "-"),
      }),
    [t]
  );

  const subscriptionStatusLabel = useCallback(
    (status) =>
      t(`billingPage.subscriptionStatus.${String(status || "").toLowerCase()}`, {
        defaultValue: String(status || "-"),
      }),
    [t]
  );

  const metricLabel = useCallback(
    (key) =>
      t(`billingPage.metricKeys.${key}`, {
        defaultValue: String(key || "").replace(/_/g, " "),
      }),
    [t]
  );

  const loadDetailBundle = useCallback(async () => {
    if (!dominioId) return;
    setLoading(true);
    setError("");
    try {
      const [dRes, usageRes, invRes, offRes, audRes, catRes, segRes] = await Promise.all([
        getPlatformDominioAdminDetail(dominioId),
        getPlatformDominioUsageSummary(dominioId),
        listPlatformDominioInvoices(dominioId, { limit: 50 }),
        getPlatformDominioOfferAnalytics(dominioId),
        listPlatformBillingAudit(dominioId, { limit: 40 }),
        getBillingPlanCatalog(),
        getPlatformDominioWorkspaceSegment(dominioId).catch(() => null),
      ]);
      setDetail(dRes);
      setUsageData(usageRes);
      setInvoices(Array.isArray(invRes?.items) ? invRes.items : []);
      setOfferAnalytics(Array.isArray(offRes?.items) ? offRes.items : []);
      setAudit(Array.isArray(audRes?.items) ? audRes.items : []);
      setPlanCatalog(Array.isArray(catRes?.items) ? catRes.items : []);
      setSegmentValue(segRes?.segment || WorkspaceSegments.Generic);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("platformPage.loadError"));
    } finally {
      setLoading(false);
    }
  }, [dominioId, t]);

  useEffect(() => {
    loadDetailBundle();
  }, [loadDetailBundle]);

  const selectablePlans = useMemo(() => {
    const set = new Set();
    const out = [];
    for (const row of planCatalog) {
      if (!row?.sku || row?.version == null) continue;
      const key = `${row.sku}:${row.version}`;
      if (set.has(key)) continue;
      set.add(key);
      out.push({
        sku: row.sku,
        version: Number(row.version),
        label: `${row.name || row.sku} (v${row.version})`,
      });
    }
    return out;
  }, [planCatalog]);

  useEffect(() => {
    if (!selectablePlans.length) return;
    const hasPlan = selectablePlans.some(
      (p) => p.sku === assignForm.planSku && Number(p.version) === Number(assignForm.version)
    );
    if (!hasPlan) {
      setAssignForm((prev) => ({
        ...prev,
        planSku: selectablePlans[0].sku,
        version: selectablePlans[0].version,
      }));
    }
  }, [selectablePlans, assignForm.planSku, assignForm.version]);

  const handleSaveSegment = async () => {
    setSegmentSaving(true);
    setError("");
    setSuccess("");
    try {
      await setPlatformDominioWorkspaceSegment(dominioId, segmentValue);
      setSuccess(t("platformPage.segmentSaved"));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("platformPage.segmentSaveError"));
    } finally {
      setSegmentSaving(false);
    }
  };

  const handleConfirmExclude = async () => {
    const did = Number(dominioId);
    if (!Number.isFinite(did) || did <= 0) return;
    setExcluding(true);
    setError("");
    setSuccess("");
    try {
      await excludePlatformDominio(did);
      setExcludeDialogOpen(false);
      navigate("/platform/xprobase");
    } catch (err) {
      setExcludeDialogOpen(false);
      setError(err?.response?.data?.message || err?.message || t("platformPage.excludeWorkspaceError"));
    } finally {
      setExcluding(false);
    }
  };

  const handleAssign = async () => {
    const did = Number(dominioId);
    if (!Number.isFinite(did) || did <= 0) return;
    setAssigning(true);
    setError("");
    setSuccess("");
    try {
      await assignBillingSubscription({
        dominioId: did,
        planSku: assignForm.planSku,
        version: Number(assignForm.version),
        billingMode: assignForm.billingMode,
        enforceLimits: !!assignForm.enforceLimits,
        status: assignForm.status,
      });
      setSuccess(t("billingPage.adminMsgAssignOk"));
      await loadDetailBundle();
    } catch (err) {
      setError(err?.message || "Falha ao atualizar assinatura.");
    } finally {
      setAssigning(false);
    }
  };

  const sub = detail?.subscription;
  const dominioRow = detail?.dominio;
  const signals = detail?.signals;
  const tenantLegacy = detail?.tenant_legacy;

  if (!dominioId) {
    return (
      <Typography color="error">{t("platformPage.pickDomain")}</Typography>
    );
  }

  const host = dominioRow?.dominio || "";
  const bdRegistered = dominioRow?.bd != null && String(dominioRow.bd).trim() !== "";
  const schemaInferred = bdRegistered ? "" : inferSchemaFromHost(host);
  const schemaDisplay = bdRegistered ? String(dominioRow.bd).trim() : schemaInferred || "";

  return (
    <Box sx={{ pt: 1, pb: 4, maxWidth: 1200, mx: "auto" }}>
      <Button
        component={RouterLink}
        to="/platform/xprobase"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
        size="small"
        color="inherit"
      >
        {t("platformPage.backToWorkspaceList")}
      </Button>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      ) : null}

      <Dialog open={excludeDialogOpen} onClose={() => !excluding && setExcludeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("platformPage.excludeWorkspaceDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("platformPage.excludeWorkspaceDialogBody", {
              host: dominioRow?.dominio || "—",
              id: dominioRow?.id ?? dominioId,
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExcludeDialogOpen(false)} disabled={excluding} color="inherit">
            {t("platformPage.excludeWorkspaceCancel")}
          </Button>
          <Button onClick={handleConfirmExclude} disabled={excluding} color="error" variant="contained">
            {excluding ? t("platformPage.excludeWorkspaceRunning") : t("platformPage.excludeWorkspaceConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {loading && !detail ? (
        <Box sx={{ py: 8, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={36} />
            <Typography color="text.secondary">{t("platformPage.detailLoading")}</Typography>
          </Stack>
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 2.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              background: (th) =>
                `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.07)} 0%, ${alpha(th.palette.primary.main, 0.02)} 55%, ${th.palette.background.paper} 100%)`,
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                {t("platformPage.workspaceHeroKicker")}
              </Typography>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, wordBreak: "break-word", lineHeight: 1.2 }}>
                {dominioRow?.dominio || `ID ${dominioId}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                {t("platformPage.workspaceHeroCaption")}
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {sub?.subscription_status ? (
                  <Chip
                    label={subscriptionStatusLabel(sub.subscription_status)}
                    color={subscriptionStatusChipColor(sub.subscription_status)}
                    size="small"
                  />
                ) : null}
                {sub?.plan_sku ? (
                  <Chip
                    icon={<SubscriptionsOutlinedIcon />}
                    label={`${sub.plan_name || sub.plan_sku} · v${sub.plan_version}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ) : (
                  <Chip label={t("platformPage.noActiveSubscription")} variant="outlined" size="small" />
                )}
                {schemaDisplay ? (
                  <Tooltip
                    title={
                      bdRegistered
                        ? t("platformPage.schemaTooltipRegistered")
                        : t("platformPage.schemaHintInferred", { schema: schemaDisplay })
                    }
                  >
                    <Chip
                      icon={<StorageOutlinedIcon />}
                      label={
                        bdRegistered
                          ? `${t("platformPage.schemaChipRegistered")}: ${schemaDisplay}`
                          : `${t("platformPage.schemaInferred")}: ${schemaDisplay}`
                      }
                      variant={bdRegistered ? "outlined" : "filled"}
                      color={bdRegistered ? "default" : "warning"}
                      size="small"
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={t("platformPage.schemaTooltipMissing")}>
                    <Chip
                      icon={<StorageOutlinedIcon />}
                      label={t("platformPage.schemaUnknown")}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                )}
                <Chip
                  label={t("platformPage.chipInternalId", { id: dominioRow?.id ?? dominioId })}
                  size="small"
                  variant="outlined"
                  sx={{ borderStyle: "dashed" }}
                />
              </Stack>
            </Stack>
          </Paper>

          {signals?.near_plan_limit?.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{t("platformPage.signalNearLimit")}</Typography>
              <ul style={{ margin: "8px 0 0 16px", padding: 0 }}>
                {signals.near_plan_limit.map((x) => (
                  <li key={x.metric_key}>
                    {metricLabel(x.metric_key)}: {x.used} / {x.limit} ({x.pct_used}%)
                  </li>
                ))}
              </ul>
            </Alert>
          ) : null}

          {signals?.period_compare?.some((r) => r.usage_trend_down) ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("platformPage.signalUsageDown")}
            </Alert>
          ) : null}

          {signals?.expansion_opportunity_hint ? (
            <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
              {t("platformPage.signalExpansionHint")}
            </Alert>
          ) : null}

          <Tabs
            value={innerTab}
            onChange={(_, v) => setInnerTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 2,
              "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontWeight: 600 },
            }}
          >
            <Tab label={t("platformPage.detailTabOverview")} />
            <Tab label={t("platformPage.detailTabUsage")} />
            <Tab label={t("platformPage.detailTabInvoices")} />
            <Tab label={t("platformPage.detailTabOffers")} />
            <Tab label={t("platformPage.detailTabSegment")} />
            <Tab label={t("platformPage.detailTabPlan")} />
            <Tab label={t("platformPage.detailTabAudit")} />
          </Tabs>

          {innerTab === 0 ? (
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", borderRadius: 2, boxShadow: (th) => `0 1px 2px ${alpha(th.palette.common.black, 0.06)}` }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <SubscriptionsOutlinedIcon color="primary" fontSize="small" />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {t("platformPage.cardSubscription")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("platformPage.subscriptionCardHint")}
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    {sub ? (
                      <Stack spacing={2}>
                        <OverviewField label={t("platformPage.rowBillingMode")}>
                          {billingModeLabel(sub.billing_mode)}
                        </OverviewField>
                        <OverviewField label={t("platformPage.rowEnforceLimits")}>
                          {Number(sub.enforce_limits)
                            ? t("platformPage.rowEnforceLimitsOn")
                            : t("platformPage.rowEnforceLimitsOff")}
                        </OverviewField>
                        <OverviewField label={t("platformPage.rowPeriod")}>
                          {fmtDate(sub.current_period_start)} — {fmtDate(sub.current_period_end)}
                        </OverviewField>
                      </Stack>
                    ) : (
                      <Alert severity="info" variant="outlined">
                        {t("platformPage.noActiveSubscriptionDetail")}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{ borderRadius: 2, boxShadow: (th) => `0 1px 2px ${alpha(th.palette.common.black, 0.06)}` }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                      {t("platformPage.detailCardDominioLegacy")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {t("platformPage.detailCardDominioLegacyHint")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <OverviewField label={t("platformPage.colWorkspaceCreated")}>{fmtDate(dominioRow?.data_cadastro)}</OverviewField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OverviewField label={t("platformPage.rowContractedUsers")}>
                          {dominioRow?.usuarios_contratados != null ? Number(dominioRow.usuarios_contratados).toLocaleString() : "—"}
                        </OverviewField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OverviewField label={t("platformPage.colLastTenantLogin")}>
                          {fmtDate(tenantLegacy?.tenant_last_user_access_at)}
                        </OverviewField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OverviewField label={t("platformPage.rowLegacyInteractions")}>
                          {tenantLegacy?.legacy_interactions_30d != null
                            ? Number(tenantLegacy.legacy_interactions_30d).toLocaleString()
                            : "—"}
                        </OverviewField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OverviewField label={t("platformPage.rowBillingInteractions")}>
                          {tenantLegacy?.interactions_billing_30d != null
                            ? Number(tenantLegacy.interactions_billing_30d).toLocaleString()
                            : "—"}
                        </OverviewField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <OverviewField label={t("platformPage.rowEffectiveInteractions")}>
                          {tenantLegacy?.usage_interactions_30d_effective != null
                            ? Number(tenantLegacy.usage_interactions_30d_effective).toLocaleString()
                            : "—"}
                        </OverviewField>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", borderRadius: 2, boxShadow: (th) => `0 1px 2px ${alpha(th.palette.common.black, 0.06)}` }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
                      <CompareArrowsIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {t("platformPage.detailSignalsTitle")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t("platformPage.usageCompareCardHint")}
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    {signals?.period_compare?.length ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("platformPage.metric")}</TableCell>
                              <TableCell align="right">{t("platformPage.usageCurrent")}</TableCell>
                              <TableCell align="right">{t("platformPage.usagePrevious")}</TableCell>
                              <TableCell align="right">Δ %</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {signals.period_compare.map((r) => (
                              <TableRow key={r.metric_key} hover>
                                <TableCell>{metricLabel(r.metric_key)}</TableCell>
                                <TableCell align="right">{Number(r.current).toLocaleString()}</TableCell>
                                <TableCell align="right">{Number(r.previous).toLocaleString()}</TableCell>
                                <TableCell align="right">
                                  {r.pct_change == null ? "—" : `${r.pct_change}%`}
                                  {r.usage_trend_down ? (
                                    <Chip size="small" label={t("platformPage.badge.usage_trend_down")} sx={{ ml: 1 }} />
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box
                        sx={{
                          py: 3,
                          px: 2,
                          textAlign: "center",
                          borderRadius: 2,
                          border: "1px dashed",
                          borderColor: "divider",
                          bgcolor: (th) => alpha(th.palette.primary.main, 0.04),
                        }}
                      >
                        <BarChartOutlinedIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          {t("platformPage.usageEmptyTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: "auto" }}>
                          {t("platformPage.usageEmptyBody")}
                        </Typography>
                        <Button size="small" variant="outlined" sx={{ mt: 2 }} onClick={() => setInnerTab(1)}>
                          {t("platformPage.usageEmptyCta")}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : null}

          {innerTab === 1 ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {usageData?.period
                    ? t("billingPage.usagePeriod", {
                        from: usageData.period.from,
                        to: usageData.period.to,
                      })
                    : ""}
                </Typography>
                {usageData?.legacy_fallback ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t("platformPage.usageLegacyFallbackNote")}
                  </Alert>
                ) : null}
                {usageData?.metrics?.length ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("platformPage.metric")}</TableCell>
                          <TableCell align="right">{t("platformPage.quantity")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageData.metrics.map((m) => (
                          <TableRow key={m.metric_key}>
                            <TableCell>{metricLabel(m.metric_key)}</TableCell>
                            <TableCell align="right">{Number(m.total_quantity).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("billingPage.usageEmpty")}
                  </Typography>
                )}
                <Button size="small" sx={{ mt: 2 }} onClick={() => loadDetailBundle()}>
                  {t("billingPage.refresh")}
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {innerTab === 2 ? (
            <TableContainer variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("platformPage.invoiceColNumber")}</TableCell>
                    <TableCell>{t("platformPage.invoiceColStatus")}</TableCell>
                    <TableCell align="right">{t("platformPage.invoiceTotal")}</TableCell>
                    <TableCell>{t("platformPage.due")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("billingPage.invoicesEmptyTitle")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{m.number_code || m.id}</TableCell>
                        <TableCell>{m.status}</TableCell>
                        <TableCell align="right">{centsToBrl(m.total_cents)}</TableCell>
                        <TableCell>{fmtDate(m.due_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          {innerTab === 3 ? (
            offerAnalytics.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                {t("platformPage.offerAnalyticsEmpty")}
              </Alert>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {offerAnalytics.map((row) => (
                  <Chip key={row.event_type} size="small" color="primary" variant="outlined" label={`${row.event_type}: ${row.total}`} />
                ))}
              </Stack>
            )
          ) : null}

          {innerTab === 4 ? (
            <Stack spacing={2} maxWidth={560}>
              <Typography variant="body2" color="text.secondary">
                {t("platformPage.segmentIntro")}
              </Typography>
              <TextField
                select
                fullWidth
                label={t("platformPage.segmentSelectValue")}
                value={segmentValue}
                onChange={(e) => setSegmentValue(e.target.value)}
                disabled={segmentSaving}
              >
                {ALL_WORKSPACE_SEGMENTS.map((seg) => (
                  <MenuItem key={seg} value={seg}>
                    {t(`platformPage.segmentOption.${seg}`, { defaultValue: seg })}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={handleSaveSegment} disabled={segmentSaving}>
                {segmentSaving ? t("platformPage.segmentSaving") : t("platformPage.segmentSave")}
              </Button>
            </Stack>
          ) : null}

          {innerTab === 5 ? (
            <Grid container spacing={2} maxWidth={720}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {t("platformPage.assignIntro")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t("billingPage.adminFieldPlanSelect")}
                  value={selectablePlans.length ? `${assignForm.planSku}:${assignForm.version}` : ""}
                  onChange={(e) => {
                    const [sku, version] = String(e.target.value || "").split(":");
                    setAssignForm((prev) => ({
                      ...prev,
                      planSku: sku,
                      version: Number(version || 1),
                    }));
                  }}
                  disabled={!selectablePlans.length}
                >
                  {selectablePlans.map((p) => (
                    <MenuItem key={`${p.sku}:${p.version}`} value={`${p.sku}:${p.version}`}>
                      {p.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t("billingPage.adminFieldBillingMode")}
                  value={assignForm.billingMode}
                  onChange={(e) => setAssignForm((prev) => ({ ...prev, billingMode: e.target.value }))}
                >
                  <MenuItem value="prepaid">{billingModeLabel("prepaid")}</MenuItem>
                  <MenuItem value="postpaid">{billingModeLabel("postpaid")}</MenuItem>
                  <MenuItem value="hybrid">{billingModeLabel("hybrid")}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t("billingPage.adminFieldSubscriptionStatus")}
                  value={assignForm.status}
                  onChange={(e) => setAssignForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="trialing">{subscriptionStatusLabel("trialing")}</MenuItem>
                  <MenuItem value="active">{subscriptionStatusLabel("active")}</MenuItem>
                  <MenuItem value="past_due">{subscriptionStatusLabel("past_due")}</MenuItem>
                  <MenuItem value="suspended">{subscriptionStatusLabel("suspended")}</MenuItem>
                  <MenuItem value="cancelled">{subscriptionStatusLabel("cancelled")}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t("billingPage.adminFieldEnforceLimits")}
                  value={assignForm.enforceLimits ? "1" : "0"}
                  onChange={(e) =>
                    setAssignForm((prev) => ({ ...prev, enforceLimits: e.target.value === "1" }))
                  }
                >
                  <MenuItem value="0">{t("billingPage.no")}</MenuItem>
                  <MenuItem value="1">{t("billingPage.yes")}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" size="large" onClick={handleAssign} disabled={assigning}>
                  {assigning ? t("billingPage.adminAssignSaving") : t("billingPage.adminAssignSubmit")}
                </Button>
              </Grid>
            </Grid>
          ) : null}

          {innerTab === 6 ? (
            <TableContainer variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("platformPage.auditWhen")}</TableCell>
                    <TableCell>{t("platformPage.auditAction")}</TableCell>
                    <TableCell>{t("platformPage.auditActor")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audit.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          {t("platformPage.auditEmpty")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    audit.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{fmtDate(a.created_at)}</TableCell>
                        <TableCell>{a.action}</TableCell>
                        <TableCell>{a.actor_user_id ?? "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          <Card
            variant="outlined"
            sx={{
              mt: 3,
              borderRadius: 2,
              borderColor: "error.light",
              bgcolor: (th) => alpha(th.palette.error.main, 0.04),
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" color="error" fontWeight={700} gutterBottom>
                {t("platformPage.excludeWorkspaceSectionTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                {t("platformPage.excludeWorkspaceSectionHint")}
              </Typography>
              <Button color="error" variant="outlined" onClick={() => setExcludeDialogOpen(true)} disabled={loading}>
                {t("platformPage.excludeWorkspaceOpenDialog")}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default WorkspaceDetailPage;
